import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Download, Hourglass, ClipboardList } from "lucide-react";
import { api, API } from "../lib/api";

export default function ShopSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error" });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const { data } = await api.get(`/api/shop/status/${sessionId}`);
        if (cancelled) return;
        if (data.paid) {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        if (attempts >= 10) {
          setState({ status: "processing" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (err.response?.status === 404) {
          setState({ status: "error" });
          return;
        }
        if (attempts >= 10) {
          setState({ status: "processing" });
          return;
        }
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const isService = state.data?.is_service;

  return (
    <div className="container-jcc py-24 max-w-xl" data-testid="page-shop-success">
      {state.status === "loading" && (
        <div className="text-center" data-testid="shop-success-loading">
          <Clock className="w-10 h-10 mx-auto text-teal animate-pulse" />
          <h1 className="mt-4 font-display text-3xl font-bold">Confirming your payment…</h1>
          <p className="mt-2 text-ink-muted">This usually takes a few seconds.</p>
        </div>
      )}
      {state.status === "paid" && isService && (
        <div className="text-center" data-testid="shop-success-service">
          <CheckCircle2 className="w-12 h-12 mx-auto text-teal" />
          <h1 className="mt-4 font-display text-3xl font-bold">Thank you — your payment is confirmed.</h1>
          <p className="mt-2 text-ink-muted">
            {state.data.product_title} · {state.data.price_display}
          </p>
          {state.data.questionnaire_token && (
            <Link to={`/shop/questionnaire/${state.data.questionnaire_token}`} className="btn-teal mt-5 inline-flex" data-testid="shop-success-questionnaire-link">
              <ClipboardList className="w-4 h-4" /> Complete your questionnaire
            </Link>
          )}
          {state.data.return_instructions && (
            <p className="mt-4 text-ink-muted whitespace-pre-line text-left rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="shop-success-return-instructions">
              {state.data.return_instructions}
            </p>
          )}
          {state.data.turnaround_time && (
            <p className="mt-3 text-sm text-ink-muted flex items-center justify-center gap-1.5" data-testid="shop-success-turnaround">
              <Hourglass className="w-4 h-4" /> Expected turnaround: {state.data.turnaround_time}
            </p>
          )}
          <p className="mt-4 text-sm text-ink-muted">We've also emailed you a link to the questionnaire — you can save your progress and finish it later.</p>
        </div>
      )}
      {state.status === "paid" && !isService && (
        <div className="text-center" data-testid="shop-success-paid">
          <CheckCircle2 className="w-12 h-12 mx-auto text-teal" />
          <h1 className="mt-4 font-display text-3xl font-bold">Thank you — your download is ready.</h1>
          <p className="mt-2 text-ink-muted">
            {state.data.product_title} · {state.data.price_display}
          </p>
          <a
            href={`${API}/shop/download/${state.data.download_token}`}
            className="btn-teal mt-6 inline-flex"
            data-testid="shop-success-download-btn"
          >
            <Download className="w-4 h-4" /> Download your PDF
          </a>
          {(state.data.attachment_filenames || []).map((name, i) => (
            <a key={i} href={`${API}/shop/download/${state.data.download_token}/attachment/${i}`}
              className="btn-outline mt-3 inline-flex" data-testid={`shop-success-attachment-${i}`}>
              <Download className="w-4 h-4" /> {name}
            </a>
          ))}
          <p className="mt-4 text-sm text-ink-muted">
            We've also emailed you a secure download link in case you need it again.
          </p>
        </div>
      )}
      {state.status === "processing" && (
        <div className="text-center" data-testid="shop-success-processing">
          <Clock className="w-12 h-12 mx-auto text-teal" />
          <h1 className="mt-4 font-display text-3xl font-bold">Payment received — finalising.</h1>
          <p className="mt-3 text-ink-muted">
            Thanks. Your payment is being confirmed — this usually takes under a minute. We'll also email you.
          </p>
          <Link to="/contact" className="btn-outline mt-6 inline-flex">
            Contact us
          </Link>
        </div>
      )}
      {state.status === "expired" && (
        <div className="text-center" data-testid="shop-success-expired">
          <XCircle className="w-12 h-12 mx-auto text-amber-500" />
          <h1 className="mt-4 font-display text-3xl font-bold">Checkout session expired.</h1>
          <p className="mt-2 text-ink-muted">No charge was made. You can start a new purchase whenever you're ready.</p>
          <Link to="/shop" className="btn-teal mt-6 inline-flex">
            Back to JCC Shop
          </Link>
        </div>
      )}
      {state.status === "error" && (
        <div className="text-center" data-testid="shop-success-error">
          <XCircle className="w-12 h-12 mx-auto text-red-600" />
          <h1 className="mt-4 font-display text-3xl font-bold">We couldn't find that session.</h1>
          <p className="mt-2 text-ink-muted">
            Please contact us if you've been charged but haven't received your download.
          </p>
          <Link to="/contact" className="btn-outline mt-6 inline-flex">
            Contact us
          </Link>
        </div>
      )}
    </div>
  );
}
